import {closeCurrentDialog, openDialog, DialogsContext} from 'platform/components';
import {Show, Text} from 'platform/foundation';
import {match} from 'ts-pattern';

import {use} from 'react';

import {filter, isNil, isNotNil, sortBy} from 'ramda';
import {isEmptyArray, isNilOrEmpty, isNotNilOrEmpty} from 'ramda-adjunct';

import {
  IntegrationActionKey,
  useGetServiceOrderIntegrationsQuery,
  usePutServiceOrderIntegrationMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {handleApiError} from '@dms/shared';
import {useServiceCaseDetailActions} from '@dms/shared-aftersales';

import {TestIdProps, openInNewTab, suffixTestId} from 'shared/web';

import {IntegrationPopper} from '../../../../../components/IntegrationPopper/IntegrationPopper';
import {AudatexCalculations} from './AudatexCalculations';
import {ClaraDetailDialog} from './ClaraDetailDialog';
import {ClaraSendDialog} from './ClaraSendDialog';
import {IntegrationItem} from './IntegrationItem';
import {ORDER_INTEGRATION_ACTION_PREFIX} from './IntegrationsDropdown/constants/actionKeys';
import {OrderTrackedTime} from './OrderTrackedTime';
import {SilverdatCalculations} from './SilverdatCalculations';

interface IntegrationsDropdownProps extends TestIdProps {
  serviceCaseId: string;
  serviceOrderId: string;
  isExternalActionPending?: boolean;
  isOrderReadOnly?: boolean;
}

export function IntegrationsDropdown(props: IntegrationsDropdownProps) {
  const {executeAction, isActionPending} = useServiceCaseDetailActions();

  const {data, isLoading, isError, refetch} = useGetServiceOrderIntegrationsQuery(
    {
      serviceCaseId: props.serviceCaseId,
      serviceOrderId: props.serviceOrderId,
    },
    {skip: isNilOrEmpty(props.serviceCaseId) || isNilOrEmpty(props.serviceOrderId)}
  );
  const [putServiceOrderIntegration] = usePutServiceOrderIntegrationMutation();

  const {isAnyDialogOpen} = use(DialogsContext);

  const integrations = sortBy(
    (integration) => integration?.position ?? 0,
    data?.orderIntegration ?? []
  );
  const activeIntegrations = filter(
    (integration) =>
      isNotNil(integration?.integration?.createdAt) ||
      isNotNil(integration?.integration?.updatedAt),
    integrations
  );
  const availableIntegrations = filter(
    (integration) =>
      isNil(integration?.integration?.createdAt) && isNil(integration?.integration?.updatedAt),
    integrations
  );
  const hasActiveIntegrations = isNotNilOrEmpty(activeIntegrations);
  const hasAvailableIntegrations = isNotNilOrEmpty(availableIntegrations);

  const onToggle = () => {
    if (isNilOrEmpty(props.serviceCaseId) || isNilOrEmpty(props.serviceOrderId)) {
      return;
    }

    refetch();
  };

  const onActionClick = (actionKey?: IntegrationActionKey) => {
    if (isNil(actionKey)) {
      return;
    }

    const integrationActionKey = `${ORDER_INTEGRATION_ACTION_PREFIX}${actionKey}`;

    match(actionKey)
      .with('chronosContractReport', () =>
        openDialog(
          <OrderTrackedTime
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.serviceOrderId}
          />,
          {title: i18n.t('entity.orderRequest.labels.workReport')}
        )
      )
      .with('audatexTaskCalculations', () =>
        openDialog(
          <AudatexCalculations
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.serviceOrderId}
            onClose={closeCurrentDialog}
            data-testid={suffixTestId('audatexCalculations', props)}
          />,
          {
            title: i18n.t('entity.orderRequest.labels.selectCalculation'),
            size: 'large',
            withAdditionalFooter: true,
          }
        )
      )
      .with('silverdatMyclaimContractCalculations', () =>
        openDialog(
          <SilverdatCalculations
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.serviceOrderId}
            onClose={closeCurrentDialog}
            data-testid={suffixTestId('audatexCalculations', props)}
          />,
          {
            title: i18n.t('entity.orderRequest.labels.selectCalculation'),
            size: 'large',
            withAdditionalFooter: true,
          }
        )
      )
      .with('daimlerClaraClaimSend', () =>
        openDialog(
          <ClaraSendDialog
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.serviceOrderId}
            onClose={closeCurrentDialog}
          />,
          {
            title: i18n.t('entity.orderIntegration.labels.daimlerClara'),
            withAdditionalFooter: true,
          }
        )
      )
      .with('daimlerClaraClaimDetail', () =>
        openDialog(
          <ClaraDetailDialog
            serviceCaseId={props.serviceCaseId}
            serviceOrderId={props.serviceOrderId}
            onClose={closeCurrentDialog}
          />,
          {
            title: i18n.t('entity.orderIntegration.labels.daimlerClara'),
            withAdditionalFooter: true,
          }
        )
      )
      .otherwise((key) => {
        executeAction(integrationActionKey, () =>
          putServiceOrderIntegration({
            serviceCaseId: props.serviceCaseId,
            serviceOrderId: props.serviceOrderId,
            body: {actionKey: key},
          })
            .unwrap()
            .then((data) => {
              if (isNotNilOrEmpty(data?.urlToOpen)) {
                openInNewTab(data?.urlToOpen!);
              }
            })
            .catch(handleApiError)
        );
      });
  };

  return (
    <IntegrationPopper
      buttonTitle={i18n.t('entity.orderIntegration.labels.integration')}
      buttonVariant="outlined"
      buttonSize="small"
      isButtonDisabled={props.isExternalActionPending || props.isOrderReadOnly}
      isLoading={isLoading}
      isError={isError}
      isEmpty={isEmptyArray(integrations)}
      onToggle={onToggle}
      isOutsideClickIgnored={isAnyDialogOpen}
      data-testid={props['data-testid']}
    >
      <Show when={hasActiveIntegrations}>
        <Text color="secondary">{i18n.t('entity.integration.labels.activeIntegrations')}</Text>
        {activeIntegrations.map((integration, index) => (
          <IntegrationItem
            key={`active-${integration?.integration?.code}-${index}`}
            integration={integration}
            isActionPending={isActionPending}
            onActionClick={onActionClick}
            isExternalActionPending={props.isExternalActionPending}
            data-testid={props['data-testid']}
          />
        ))}
      </Show>
      <Show when={hasAvailableIntegrations}>
        <Text color="secondary">{i18n.t('entity.integration.labels.availableIntegrations')}</Text>
        {availableIntegrations.map((integration, index) => (
          <IntegrationItem
            key={`available-${integration?.integration?.code}-${index}`}
            integration={integration}
            isActionPending={isActionPending}
            onActionClick={onActionClick}
            isExternalActionPending={props.isExternalActionPending}
            data-testid={props['data-testid']}
          />
        ))}
      </Show>
    </IntegrationPopper>
  );
}
